class BaseStruct {
    constructor(result, ...args) {
        this.args = args;
        this._result = result;
    }
    setup() {
        this._unit = isUnit(this._result) ? this._result : this.resultUnit();
        this.result = this._result === null || isUnit(this._result) ? this.value : this._result;
    }

    get canBeCalculated() {
        return this.args.every(a => typeof (a) !== 'string');
    }

    get v() {
        return val(this);
    }
}


export const Unit = {
    RUBLE: 'ruble',
    PRECISE: 'precise',
    COUNT: 'count',
    SUM: 'sum',
    PERCENT: 'percent'
}


const isUnit = (v) => {
    return [Unit.RUBLE, Unit.PRECISE, Unit.COUNT, Unit.SUM, Unit.PERCENT].indexOf(v) >= 0;
}


export class UnitProp {
    constructor(value, unit = Unit.SUM) {
        this.unit = unit;
        this._value = value;
    }

    get value() {
        switch (this.unit) {
            case Unit.COUNT: return Math.round(this._value);
            case Unit.PRECISE: return tools.roundNumber(this._value, 4);
            default: return tools.roundNumber(this._value, 2);
        }
    }

    render() {
        switch (this.unit) {
            case Unit.COUNT: return `${Math.round(this._value)}`;
            case Unit.RUBLE: return tools.getLocaleSumInRubbles(this._value);
            case Unit.PRECISE: return tools.getLocaleNumber(this._value, { maxPrecision: 4 });
            case Unit.SUM: return tools.getLocaleSum(this._value);
            case Unit.PERCENT: return tools.getLocaleNumber(this._value, { minPrecision: 0, maxPrecision: 2 }) + "%";
            default: return 'Invalid Unit Value';
        }
    }

    as(unit) {
        return new UnitProp(this.value, unit);
    }

    toString() {
        return this.render();
    }
}


const val = (obj) => {
    if (typeof(obj) == 'object' && 'render' in obj)
        return obj.value;
    return obj.value?.value;
}


export class Fraction extends BaseStruct {
    constructor(num, denum, result = null) {
        super(result, num, denum);
        this.num = num;
        this.denum = denum;
        this.setup();
    }

    resultUnit() {
        if (this.denum.unit === Unit.COUNT)
            return this.num.unit;
        return Unit.PERCENT;
    }

    get value() {
        return this.canBeCalculated ? new UnitProp(val(this.num) / val(this.denum), this._unit) : null;
    }

    get html() {
        return `<div class="fraction">
            <div class="numerator">${this.num}</div>
            <div class="denominator">${this.denum}</div>
            <div class="equality">${this.result || ''}</div>
        </div>`;
    }

    get export() {
        return `${this.num} / ${this.denum}${this.result ? ' = ' + this.result : ""}`
    }
}

export class SquareRoot extends BaseStruct {
    constructor(body, result = null) {
        super(result, body);
        this.body = body;
        this.setup();
    }

    resultUnit() {
        return Unit.PRECISE;
    }

    get value() {
        return this.canBeCalculated ? new UnitProp(Math.sqrt(val(this.body)), this._unit) : null;
    }

    get html() {
        return `<div class='square-root'>
            <div class='radicand'>${this.body.html}</div>
            <div class="equality">${this.result || ''}</div>
        </div>`;
    }

    get export() {
        return `√ (${this.body.export}) ${this.result ? '= ' + this.result : ""}`
    }
}


export const calculationBullet = (prices) => {
    const values = prices.map(p => new UnitProp(p));
    const sum = new UnitProp(values.reduce((acc, v) => acc + v.value, 0));
    const mean = new Fraction(sum, new UnitProp(values.length, Unit.COUNT));
    const stdnum = new UnitProp(values.reduce((acc, v) => acc + Math.pow(+v.value - mean.v, 2), 0), Unit.PRECISE);
    const stdbody = new Fraction(stdnum, new UnitProp(values.length - 1, Unit.COUNT));
    const std = new SquareRoot(stdbody);
    const varnum = new UnitProp(std.v * 100);
    const var_ = new Fraction(varnum, mean.value);
    return {
        values, sum, mean: new Fraction(mean.num, mean.denum, Unit.RUBLE),
        stdnum, stdbody, std, varnum, var: var_, count: values.length
    };
}


